import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../../services/apiAuth';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './BusinessAnalytics.css';

/** Normalize skin type values (Korean + backend enum) to English display names */
const skinTypeDisplayMap: Record<string, string> = {
  '건성': 'Dry',
  '지성': 'Oily',
  '중성': 'Normal',
  '복합성': 'Combination',
  '민감성': 'Sensitive',
  'combo': 'Combination',
  'dry': 'Dry',
  'oily': 'Oily',
  'normal': 'Normal',
  'sensitive': 'Sensitive',
};
function normalizeSkinType(raw: string): string {
  return skinTypeDisplayMap[raw.toLowerCase()] || skinTypeDisplayMap[raw] || raw.charAt(0).toUpperCase() + raw.slice(1);
}

type DateRange = '7days' | '30days' | '90days' | 'year';
type AnalyticsTab = 'overview' | 'engagement' | 'affiliate' | 'retention';

interface UserMetrics {
  totalUsers: number;
  activeUsers7d: number;
  activeUsers30d: number;
  avgProfileCompletion: number;
  skinTypeBreakdown: Record<string, number>;
  stageBreakdown: Record<string, number>;
  languageBreakdown?: Record<string, number>;
  trends?: {
    active7dChange: number;
    active30dChange: number;
  };
}

interface ClickSummary {
  totalClicks: number;
  paidClicks: number;
  organicClicks: number;
  sourceBreakdown: Record<string, number>;
  perUser?: Record<string, number>;
}

interface BusinessAnalyticsProps {
  language?: string;
  activeView?: AnalyticsTab;
  dateRange?: DateRange;
  onDateRangeChange?: (range: DateRange) => void;
}

interface CohortRow {
  cohort: string;
  signups: number;
  returnedDay1: number;
  returnedDay7: number;
  returnedDay30: number;
  retentionDay1: number;
  retentionDay7: number;
  retentionDay30: number;
}

interface RetentionData {
  summary: {
    totalRegistered: number;
    returnedAtLeastOnce: number;
    neverReturned: number;
    neverReturnedPercent: number;
    churnedUsers: number;
  };
  cohortRetention: CohortRow[];
  onboardingFunnel: Array<{
    step: string;
    completed: number;
    percentage: number;
  }>;
  engagementCorrelation: Array<{
    action: string;
    returnedPct: number;
    neverReturnedPct: number;
  }>;
  churnedProfiles: Array<{
    uid: string;
    email: string;
    name: string;
    signupDate: string;
    lastVisitDate: string;
    daysSinceLastVisit: number;
    profileCompletion: number;
    totalMessages: number;
    customerStage: string;
    skinType: string;
    missingSteps: string[];
  }>;
  recommendations: string[];
}

const BusinessAnalytics: React.FC<BusinessAnalyticsProps> = ({ activeView, dateRange: controlledRange, onDateRangeChange }) => {
  const { t } = useTranslation('admin');
  const [loading, setLoading] = useState<boolean>(true);
  const [internalRange, setInternalRange] = useState<DateRange>('30days');
  const dateRange = controlledRange ?? internalRange;
  const setDateRange = onDateRangeChange ?? setInternalRange;
  const activeTab = activeView || 'overview';
  const [userMetrics, setUserMetrics] = useState<UserMetrics | null>(null);
  const [clickSummary, setClickSummary] = useState<ClickSummary | null>(null);
  const [retentionData, setRetentionData] = useState<RetentionData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const getDateRangeStart = useCallback((range: DateRange): Date => {
    const now = new Date();
    switch (range) {
      case '7days': return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case '30days': return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      case '90days': return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      case 'year': return new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
    }
  }, []);

  const loadUserMetrics = useCallback(async () => {
    try {
      const response = await authFetch(`${getApiBaseUrl()}/getUserMetrics`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setUserMetrics(result.data);
        } else {
          setError('Failed to load user metrics');
        }
      } else {
        setError('Failed to load user metrics');
      }
    } catch (err) {
      console.error('Error loading user metrics:', err);
      setError('Failed to load user metrics');
    }
  }, []);

  const loadClickStats = useCallback(async () => {
    try {
      const startDate = getDateRangeStart(dateRange).toISOString();
      const endDate = new Date().toISOString();
      const response = await authFetch(
        `${getApiBaseUrl()}/getClickStats?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setClickSummary(result.data.summary);
        } else {
          setClickSummary({ totalClicks: 0, paidClicks: 0, organicClicks: 0, sourceBreakdown: {} });
        }
      } else {
        setClickSummary({ totalClicks: 0, paidClicks: 0, organicClicks: 0, sourceBreakdown: {} });
      }
    } catch (error) {
      console.error('Error loading click stats:', error);
      setClickSummary({ totalClicks: 0, paidClicks: 0, organicClicks: 0, sourceBreakdown: {} });
    }
  }, [dateRange, getDateRangeStart]);

  const loadRetentionData = useCallback(async () => {
    try {
      const response = await authFetch(`${getApiBaseUrl()}/getRetentionAnalysis`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setRetentionData(result.data);
        }
      }
    } catch (err) {
      console.error('Error loading retention data:', err);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    await Promise.all([loadUserMetrics(), loadClickStats(), loadRetentionData()]);
    setLoading(false);
  }, [loadUserMetrics, loadClickStats, loadRetentionData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="business-analytics-loading">
        <div className="loading-spinner"></div>
        <p>{t('analytics.loading')}</p>
      </div>
    );
  }

  // Generate AI recommendations based on current data
  const getOverviewRecommendations = (): string[] => {
    if (!userMetrics || !clickSummary) return [];
    const recs: string[] = [];
    const activeRate = userMetrics.totalUsers > 0
      ? (userMetrics.activeUsers7d / userMetrics.totalUsers) * 100 : 0;
    if (activeRate < 20) {
      recs.push(`🔴 Only ${activeRate.toFixed(0)}% of users were active in the last 7 days. Consider re-engagement campaigns: push notifications, email drip sequences, or in-app prompts for inactive users.`);
    } else if (activeRate < 40) {
      recs.push(`🟡 ${activeRate.toFixed(0)}% 7-day active rate is moderate. Focus on onboarding optimization — users who complete their profile within 24 hours show higher retention.`);
    } else {
      recs.push(`🟢 Strong ${activeRate.toFixed(0)}% 7-day active rate. Leverage this momentum with referral incentives and community features.`);
    }
    if (userMetrics.avgProfileCompletion < 60) {
      recs.push(`📋 Average profile completion is ${userMetrics.avgProfileCompletion}%. Add progressive disclosure prompts — ask one additional question per session to boost completion without overwhelming users.`);
    }
    if (clickSummary.totalClicks > 0 && userMetrics.activeUsers30d > 0) {
      const clicksPerUser = clickSummary.totalClicks / userMetrics.activeUsers30d;
      if (clicksPerUser < 1) {
        recs.push(`🔗 Low affiliate engagement (${clicksPerUser.toFixed(1)} clicks/active user). Try surfacing product recommendations earlier in the conversation flow and adding "Shop Similar" buttons to the results page.`);
      }
    }
    if (userMetrics.trends?.active7dChange && userMetrics.trends.active7dChange < -10) {
      recs.push(`📉 Weekly active users declined ${Math.abs(userMetrics.trends.active7dChange)}%. Investigate: check for app errors in the last week, review recent UX changes, and consider a win-back campaign.`);
    }
    return recs;
  };

  const getEngagementRecommendations = (): string[] => {
    if (!userMetrics) return [];
    const recs: string[] = [];
    const skinTypes = Object.entries(userMetrics.skinTypeBreakdown);
    if (skinTypes.length > 0 && userMetrics.totalUsers > 0) {
      // Merge normalized skin types before finding top type
      const merged = new Map<string, number>();
      for (const [raw, count] of skinTypes) {
        const display = normalizeSkinType(raw);
        merged.set(display, (merged.get(display) || 0) + count);
      }
      const topType = [...merged.entries()].sort((a, b) => b[1] - a[1])[0];
      const topPct = ((topType[1] / userMetrics.totalUsers) * 100).toFixed(0);
      recs.push(`👤 ${topType[0]} skin type represents ${topPct}% of users. Create targeted content (blog posts, TikTok scripts, email sequences) specifically for ${topType[0]} skin concerns to boost engagement.`);
    }
    const stages = userMetrics.stageBreakdown;
    const visitorCount = stages['visitor'] || 0;
    const visitorPct = userMetrics.totalUsers > 0
      ? ((visitorCount / userMetrics.totalUsers) * 100).toFixed(0) : '0';
    if (parseInt(visitorPct) > 50) {
      recs.push(`🚪 ${visitorPct}% of users are still at "visitor" stage. Add a guided onboarding flow: welcome modal → skin quiz → first recommendation → save results. This is the highest-leverage improvement.`);
    }
    const churnedCount = stages['churned'] || 0;
    if (churnedCount > 0 && userMetrics.totalUsers > 10) {
      const churnPct = ((churnedCount / userMetrics.totalUsers) * 100).toFixed(0);
      recs.push(`⚠️ ${churnPct}% of users have churned. Prioritize win-back: send personalized "We updated your recommendations" emails with specific product picks based on their profile.`);
    }
    if (userMetrics.languageBreakdown) {
      const langs = Object.entries(userMetrics.languageBreakdown).sort((a, b) => b[1] - a[1]);
      if (langs.length > 1 && langs[0][1] > 0) {
        const topLang = langs[0];
        recs.push(`🌐 ${topLang[0]} users lead with ${topLang[1]} users. Double down on ${topLang[0]}-language content marketing and localized social media targeting.`);
      }
    }
    return recs;
  };

  const getAffiliateRecommendations = (): string[] => {
    if (!clickSummary) return [];
    const recs: string[] = [];
    if (clickSummary.totalClicks === 0) {
      recs.push('🔗 No affiliate clicks yet. Ensure product cards have prominent "Shop Now" CTAs, and verify affiliate links are correctly configured for all regions.');
      return recs;
    }
    if (clickSummary.paidClicks > 0) {
      const paidRate = (clickSummary.paidClicks / clickSummary.totalClicks) * 100;
      if (paidRate > 50) {
        recs.push(`💰 Sponsored clicks account for ${paidRate.toFixed(0)}% of total. This is healthy revenue but diversify by growing organic recommendations to reduce dependency on sponsored placements.`);
      } else {
        recs.push(`📊 Organic clicks (${(100 - paidRate).toFixed(0)}%) dominate. Consider selling more sponsored slots — high organic engagement signals trust, which sponsors value.`);
      }
    }
    const sources = Object.entries(clickSummary.sourceBreakdown).sort((a, b) => b[1] - a[1]);
    if (sources.length >= 2) {
      const topSource = sources[0];
      const topPct = ((topSource[1] / clickSummary.totalClicks) * 100).toFixed(0);
      if (parseInt(topPct) > 70) {
        recs.push(`⚠️ ${topSource[0]} accounts for ${topPct}% of clicks — high concentration risk. Expand partnerships with other affiliate platforms to diversify revenue streams.`);
      }
    }
    if (userMetrics) {
      const activeUsers = dateRange === '7days' ? userMetrics.activeUsers7d
        : userMetrics.activeUsers30d;
      if (activeUsers > 0) {
        const conversionRate = (clickSummary.totalClicks / activeUsers) * 100;
        if (conversionRate < 10) {
          recs.push(`📈 Only ${conversionRate.toFixed(1)}% of active users click affiliate links. Experiment with: product recommendation placement, "Top picks for your skin" widgets, and post-analysis product suggestions.`);
        }
      }
    }
    return recs;
  };

  const renderRecommendations = (recs: string[], title: string) => {
    if (recs.length === 0) return null;
    return (
      <div className="stage-distribution" style={{ marginTop: '1.5rem' }}>
        <h3>🤖 {title}</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {recs.map((rec, i) => (
            <div key={i} style={{
              padding: '0.75rem 1rem',
              background: 'rgba(139, 92, 246, 0.08)',
              borderRadius: '8px',
              borderLeft: '3px solid #8B5CF6',
              fontSize: '0.9rem',
              lineHeight: '1.5',
            }}>
              {rec}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="business-analytics">
      {/* Header with Date Range Selector */}
      <div className="business-analytics-header">
        <h2>💼 {t('analytics.title')}</h2>
        
        <div className="date-range-selector">
          <span className="date-label">
            {t('analytics.period')}
          </span>
          {(['7days', '30days', '90days', 'year'] as DateRange[]).map((range) => (
            <button
              key={range}
              className={`range-btn ${dateRange === range ? 'active' : ''}`}
              onClick={() => setDateRange(range)}
            >
              {t(`analytics.dateRange.${range}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Sub-Tab Navigation removed — controlled by AdminPage */}

      {/* Tab Content */}
      <div className="business-tab-content">
        {error && (
          <div className="analytics-error">
            <p>⚠️ {error}</p>
            <button onClick={loadData} className="retry-btn">🔄 Retry</button>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && userMetrics && (
          <div className="overview-tab">
            <div className="metrics-grid">
              <div className="metric-card revenue">
                <div className="metric-icon">👥</div>
                <div className="metric-info">
                  <div className="metric-value">{userMetrics.totalUsers}</div>
                  <div className="metric-label">
                    {t('analytics.metrics.totalUsers', 'Total Users')}
                  </div>
                </div>
              </div>

              <div className="metric-card orders">
                <div className="metric-icon">🟢</div>
                <div className="metric-info">
                  <div className="metric-value">{userMetrics.activeUsers7d}</div>
                  <div className="metric-label">
                    {t('analytics.metrics.activeUsers7d', 'Active (7 days)')}
                  </div>
                  {userMetrics.trends && userMetrics.trends.active7dChange !== 0 && (
                    <div className={`metric-trend ${userMetrics.trends.active7dChange > 0 ? 'trend-up' : 'trend-down'}`}>
                      {userMetrics.trends.active7dChange > 0 ? '↑' : '↓'} {Math.abs(userMetrics.trends.active7dChange)}% {t('analytics.retention.vsPriorWeek', 'vs prior week')}
                    </div>
                  )}
                </div>
              </div>

              <div className="metric-card avg-order">
                <div className="metric-icon">📊</div>
                <div className="metric-info">
                  <div className="metric-value">{userMetrics.avgProfileCompletion}%</div>
                  <div className="metric-label">
                    {t('analytics.metrics.avgProfile', 'Avg Profile Completion')}
                  </div>
                </div>
              </div>

              <div className="metric-card inventory-value">
                <div className="metric-icon">🔗</div>
                <div className="metric-info">
                  <div className="metric-value">{clickSummary?.totalClicks || 0}</div>
                  <div className="metric-label">
                    {t('analytics.metrics.totalClicks', 'Affiliate Clicks')}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="quick-stats">
              <h3>📊 {t('analytics.quickStats')}</h3>
              <div className="stats-row">
                <div className="stat-item">
                  <span className="stat-label">{t('analytics.stats.active30d', 'Active (30d)')}</span>
                  <span className="stat-value">{userMetrics.activeUsers30d}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">{t('analytics.stats.paidClicks', 'Paid Clicks')}</span>
                  <span className="stat-value">{clickSummary?.paidClicks || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">{t('analytics.stats.organicClicks', 'Organic Clicks')}</span>
                  <span className="stat-value">{clickSummary?.organicClicks || 0}</span>
                </div>
              </div>
            </div>

            {renderRecommendations(getOverviewRecommendations(), t('analytics.aiRecommendations', 'AI Recommendations — Action Items'))}
          </div>
        )}

        {/* Engagement Tab */}
        {activeTab === 'engagement' && userMetrics && (
          <div className="engagement-tab">
            {/* Skin Type Distribution */}
            <div className="stage-distribution">
              <h3>{t('analytics.engagement.skinTypes', 'Skin Type Distribution')}</h3>
              <div className="stage-chart">
                {(() => {
                  // Merge entries that normalize to the same display name (e.g. "combo" + "Combination" → "Combination")
                  const merged = new Map<string, number>();
                  for (const [raw, count] of Object.entries(userMetrics.skinTypeBreakdown)) {
                    const display = normalizeSkinType(raw);
                    merged.set(display, (merged.get(display) || 0) + count);
                  }
                  return [...merged.entries()]
                  .sort((a, b) => b[1] - a[1])
                  .map(([skinType, count]) => (
                    <div key={skinType} className="stage-chart-row">
                      <div className="stage-info">
                        <span className="stage-label">{skinType}</span>
                        <span className="stage-count">{count} {count === 1 ? 'user' : 'users'}</span>
                      </div>
                      <div className="stage-bar-container">
                        <div
                          className="stage-bar"
                          style={{
                            width: `${(count / userMetrics.totalUsers) * 100}%`,
                            backgroundColor: '#8B5CF6',
                          }}
                        />
                        <span className="stage-percentage">
                          {((count / userMetrics.totalUsers) * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Customer Stage Breakdown */}
            <div className="stage-distribution" style={{ marginTop: '1.5rem' }}>
              <h3>{t('analytics.engagement.stageBreakdown', 'Customer Stage Breakdown')}</h3>
              <div className="stage-chart">
                {Object.entries(userMetrics.stageBreakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([stage, count]) => {
                    const stageColors: Record<string, string> = {
                      visitor: '#9E9E9E',
                      engaged: '#607D8B',
                      registered: '#2196F3',
                      'sample-requested': '#03A9F4',
                      'sample-sent': '#FF9800',
                      'sample-delivered': '#9C27B0',
                      'feedback-given': '#00BCD4',
                      converted: '#4CAF50',
                      loyal: '#FFD700',
                      churned: '#F44336',
                    };
                    return (
                      <div key={stage} className="stage-chart-row">
                        <div className="stage-info">
                          <span className="stage-label">{stage.replace(/-/g, ' ')}</span>
                          <span className="stage-count">{count} {count === 1 ? 'user' : 'users'}</span>
                        </div>
                        <div className="stage-bar-container">
                          <div
                            className="stage-bar"
                            style={{
                              width: `${(count / userMetrics.totalUsers) * 100}%`,
                              backgroundColor: stageColors[stage] || '#999',
                            }}
                          />
                          <span className="stage-percentage">
                            {((count / userMetrics.totalUsers) * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Language Distribution */}
            {userMetrics.languageBreakdown && Object.keys(userMetrics.languageBreakdown).length > 0 && (
              <div className="stage-distribution" style={{ marginTop: '1.5rem' }}>
                <h3>🌐 {t('analytics.engagement.languages', 'Language Distribution')}</h3>
                <div className="stage-chart">
                  {Object.entries(userMetrics.languageBreakdown)
                    .sort((a, b) => b[1] - a[1])
                    .map(([lang, count]) => {
                      const langLabels: Record<string, string> = {
                        en: 'English', ko: '한국어', es: 'Español', 'pt-BR': 'Português',
                        de: 'Deutsch', fr: 'Français', it: 'Italiano', nl: 'Nederlands',
                        sv: 'Svenska', pl: 'Polski',
                      };
                      return (
                        <div key={lang} className="stage-chart-row">
                          <div className="stage-info">
                            <span className="stage-label">{langLabels[lang] || lang}</span>
                            <span className="stage-count">{count} users</span>
                          </div>
                          <div className="stage-bar-container">
                            <div
                              className="stage-bar"
                              style={{
                                width: `${(count / userMetrics.totalUsers) * 100}%`,
                                backgroundColor: '#2196F3',
                              }}
                            />
                            <span className="stage-percentage">
                              {((count / userMetrics.totalUsers) * 100).toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {renderRecommendations(getEngagementRecommendations(), t('analytics.aiRecommendations', 'AI Recommendations — Action Items'))}
          </div>
        )}

        {/* Affiliate Tab */}
        {activeTab === 'affiliate' && clickSummary && (
          <div className="affiliate-tab">
            <div className="metrics-grid">
              <div className="metric-card revenue">
                <div className="metric-icon">🖱️</div>
                <div className="metric-info">
                  <div className="metric-value">{clickSummary.totalClicks}</div>
                  <div className="metric-label">
                    {t('analytics.affiliate.totalClicks', 'Total Clicks')}
                  </div>
                </div>
              </div>

              <div className="metric-card orders">
                <div className="metric-icon">💰</div>
                <div className="metric-info">
                  <div className="metric-value">{clickSummary.paidClicks}</div>
                  <div className="metric-label">
                    {t('analytics.affiliate.paidClicks', 'Paid/Sponsored')}
                  </div>
                </div>
              </div>

              <div className="metric-card avg-order">
                <div className="metric-icon">🌱</div>
                <div className="metric-info">
                  <div className="metric-value">{clickSummary.organicClicks}</div>
                  <div className="metric-label">
                    {t('analytics.affiliate.organicClicks', 'Organic')}
                  </div>
                </div>
              </div>

              <div className="metric-card inventory-value">
                <div className="metric-icon">📈</div>
                <div className="metric-info">
                  <div className="metric-value">
                    {clickSummary.totalClicks > 0
                      ? `${((clickSummary.paidClicks / clickSummary.totalClicks) * 100).toFixed(1)}%`
                      : '0%'}
                  </div>
                  <div className="metric-label">
                    {t('analytics.affiliate.paidRate', 'Paid Click Rate')}
                  </div>
                </div>
              </div>
            </div>

            {/* Source Breakdown */}
            {Object.keys(clickSummary.sourceBreakdown).length > 0 && (
              <div className="stage-distribution" style={{ marginTop: '1.5rem' }}>
                <h3>{t('analytics.affiliate.sourceBreakdown', 'Click Source Breakdown')}</h3>
                <div className="stage-chart">
                  {Object.entries(clickSummary.sourceBreakdown)
                    .sort((a, b) => b[1] - a[1])
                    .map(([source, count]) => {
                      const sourceColors: Record<string, string> = {
                        amazon: '#FF9900',
                        coupang: '#E44232',
                        iherb: '#5B8930',
                        yesstyle: '#000000',
                        organic: '#9E9E9E',
                      };
                      return (
                        <div key={source} className="stage-chart-row">
                          <div className="stage-info">
                            <span className="stage-label">{source}</span>
                            <span className="stage-count">{count} clicks</span>
                          </div>
                          <div className="stage-bar-container">
                            <div
                              className="stage-bar"
                              style={{
                                width: `${(count / clickSummary.totalClicks) * 100}%`,
                                backgroundColor: sourceColors[source] || '#8B5CF6',
                              }}
                            />
                            <span className="stage-percentage">
                              {((count / clickSummary.totalClicks) * 100).toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {clickSummary.totalClicks === 0 && (
              <div className="chart-placeholder">
                <div className="chart-icon">🔗</div>
                <p>{t('analytics.affiliate.noClicks', 'No affiliate clicks recorded in this period.')}</p>
              </div>
            )}

            {/* Per-User Click Breakdown */}
            {clickSummary.perUser && Object.keys(clickSummary.perUser).length > 0 && (
              <div className="stage-distribution" style={{ marginTop: '1.5rem' }}>
                <h3>{t('analytics.affiliate.perUser', 'Clicks Per User')}</h3>
                <div className="stage-chart">
                  {Object.entries(clickSummary.perUser)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 20)
                    .map(([userId, count]) => (
                      <div key={userId} className="stage-chart-row">
                        <div className="stage-info">
                          <span className="stage-label" title={userId}>
                            {userId.length > 12 ? `${userId.slice(0, 12)}…` : userId}
                          </span>
                          <span className="stage-count">{count} clicks</span>
                        </div>
                        <div className="stage-bar-container">
                          <div
                            className="stage-bar"
                            style={{
                              width: `${clickSummary.totalClicks > 0 ? (count / clickSummary.totalClicks) * 100 : 0}%`,
                              backgroundColor: '#2196F3',
                            }}
                          />
                          <span className="stage-percentage">
                            {clickSummary.totalClicks > 0 ? ((count / clickSummary.totalClicks) * 100).toFixed(1) : '0.0'}%
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {renderRecommendations(getAffiliateRecommendations(), t('analytics.aiRecommendations', 'AI Recommendations — Action Items'))}
          </div>
        )}

        {/* Retention Tab */}
        {activeTab === 'retention' && retentionData && (
          <div className="retention-tab">
            {/* Summary Cards */}
            <div className="metrics-grid">
              <div className="metric-card revenue">
                <div className="metric-icon">👥</div>
                <div className="metric-info">
                  <div className="metric-value">{retentionData.summary.totalRegistered}</div>
                  <div className="metric-label">{t('analytics.retention.totalRegistered', 'Total Registered')}</div>
                </div>
              </div>
              <div className="metric-card orders">
                <div className="metric-icon">🔁</div>
                <div className="metric-info">
                  <div className="metric-value">{retentionData.summary.returnedAtLeastOnce}</div>
                  <div className="metric-label">{t('analytics.retention.returnedAtLeastOnce', 'Returned At Least Once')}</div>
                </div>
              </div>
              <div className="metric-card avg-order" style={{ borderColor: '#F44336' }}>
                <div className="metric-icon">🚪</div>
                <div className="metric-info">
                  <div className="metric-value">{retentionData.summary.neverReturnedPercent}%</div>
                  <div className="metric-label">{t('analytics.retention.neverReturned', 'Never Returned')}</div>
                </div>
              </div>
              <div className="metric-card inventory-value">
                <div className="metric-icon">⚠️</div>
                <div className="metric-info">
                  <div className="metric-value">{retentionData.summary.churnedUsers}</div>
                  <div className="metric-label">{t('analytics.retention.churned14d', 'Churned (14+ days)')}</div>
                </div>
              </div>
            </div>

            {/* AI Recommendations */}
            {retentionData.recommendations.length > 0 && (
              <div className="stage-distribution" style={{ marginTop: '1.5rem' }}>
                <h3>💡 {t('analytics.retention.aiRecommendations', 'AI Recommendations')}</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {retentionData.recommendations.map((rec, i) => (
                    <div key={i} style={{
                      padding: '0.75rem 1rem',
                      background: 'rgba(139, 92, 246, 0.08)',
                      borderRadius: '8px',
                      borderLeft: '3px solid #8B5CF6',
                      fontSize: '0.9rem',
                      lineHeight: '1.5',
                    }}>
                      {rec}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cohort Retention */}
            {retentionData.cohortRetention.length > 0 && (
              <div className="stage-distribution" style={{ marginTop: '1.5rem' }}>
                <h3>📅 {t('analytics.retention.cohortRetention', 'Cohort Retention (by signup week)')}</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border-color, #e0e0e0)' }}>
                        <th style={{ textAlign: 'left', padding: '0.5rem' }}>{t('analytics.retention.cohort', 'Cohort')}</th>
                        <th style={{ textAlign: 'right', padding: '0.5rem' }}>{t('analytics.retention.signups', 'Signups')}</th>
                        <th style={{ textAlign: 'right', padding: '0.5rem' }}>Day-1</th>
                        <th style={{ textAlign: 'right', padding: '0.5rem' }}>Day-7</th>
                        <th style={{ textAlign: 'right', padding: '0.5rem' }}>Day-30</th>
                      </tr>
                    </thead>
                    <tbody>
                      {retentionData.cohortRetention.map((row) => (
                        <tr key={row.cohort} style={{ borderBottom: '1px solid var(--border-color, #e0e0e0)' }}>
                          <td style={{ padding: '0.5rem', fontWeight: 500 }}>{row.cohort}</td>
                          <td style={{ textAlign: 'right', padding: '0.5rem' }}>{row.signups}</td>
                          <td style={{ textAlign: 'right', padding: '0.5rem' }}>
                            <span style={{ color: row.retentionDay1 > 50 ? '#4CAF50' : row.retentionDay1 > 20 ? '#FF9800' : '#F44336' }}>
                              {row.retentionDay1}%
                            </span>
                            <span style={{ color: '#888', fontSize: '0.75rem' }}> ({row.returnedDay1})</span>
                          </td>
                          <td style={{ textAlign: 'right', padding: '0.5rem' }}>
                            <span style={{ color: row.retentionDay7 > 30 ? '#4CAF50' : row.retentionDay7 > 10 ? '#FF9800' : '#F44336' }}>
                              {row.retentionDay7}%
                            </span>
                            <span style={{ color: '#888', fontSize: '0.75rem' }}> ({row.returnedDay7})</span>
                          </td>
                          <td style={{ textAlign: 'right', padding: '0.5rem' }}>
                            <span style={{ color: row.retentionDay30 > 20 ? '#4CAF50' : row.retentionDay30 > 5 ? '#FF9800' : '#F44336' }}>
                              {row.retentionDay30}%
                            </span>
                            <span style={{ color: '#888', fontSize: '0.75rem' }}> ({row.returnedDay30})</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Onboarding Funnel */}
            <div className="stage-distribution" style={{ marginTop: '1.5rem' }}>
                <h3>📊 {t('analytics.retention.onboardingFunnel', 'Onboarding Funnel')}</h3>
              <div className="stage-chart">
                {retentionData.onboardingFunnel.map(({ step, completed, percentage }) => (
                  <div key={step} className="stage-chart-row">
                    <div className="stage-info">
                      <span className="stage-label">{step}</span>
                      <span className="stage-count">{completed} users</span>
                    </div>
                    <div className="stage-bar-container">
                      <div
                        className="stage-bar"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: percentage > 70 ? '#4CAF50' : percentage > 40 ? '#FF9800' : '#F44336',
                        }}
                      />
                      <span className="stage-percentage">{percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Engagement-Retention Correlation */}
            <div className="stage-distribution" style={{ marginTop: '1.5rem' }}>
                <h3>🔗 {t('analytics.retention.whatPredicts', 'What Actions Predict Returns?')}</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-color, #e0e0e0)' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>{t('analytics.retention.action', 'Action')}</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem', color: '#4CAF50' }}>{t('analytics.retention.returnedPct', 'Returned (%)')}</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem', color: '#F44336' }}>{t('analytics.retention.neverReturnedPct', 'Never Returned (%)')}</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem' }}>{t('analytics.retention.gap', 'Gap')}</th>
                  </tr>
                </thead>
                <tbody>
                  {retentionData.engagementCorrelation.map((row) => {
                    const gap = row.returnedPct - row.neverReturnedPct;
                    return (
                      <tr key={row.action} style={{ borderBottom: '1px solid var(--border-color, #e0e0e0)' }}>
                        <td style={{ padding: '0.5rem' }}>{row.action}</td>
                        <td style={{ textAlign: 'right', padding: '0.5rem', color: '#4CAF50', fontWeight: 600 }}>{row.returnedPct}%</td>
                        <td style={{ textAlign: 'right', padding: '0.5rem', color: '#F44336', fontWeight: 600 }}>{row.neverReturnedPct}%</td>
                        <td style={{ textAlign: 'right', padding: '0.5rem', fontWeight: 700, color: gap > 20 ? '#4CAF50' : '#666' }}>
                          {gap > 0 ? '+' : ''}{gap}pp
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Churned User Profiles */}
            {retentionData.churnedProfiles.length > 0 && (
              <div className="stage-distribution" style={{ marginTop: '1.5rem' }}>
                <h3>⚠️ {t('analytics.retention.churnedUsers', 'Churned Users')} ({retentionData.churnedProfiles.length})</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border-color, #e0e0e0)' }}>
                        <th style={{ textAlign: 'left', padding: '0.5rem' }}>{t('analytics.retention.name', 'Name')}</th>
                        <th style={{ textAlign: 'left', padding: '0.5rem' }}>{t('analytics.retention.signup', 'Signup')}</th>
                        <th style={{ textAlign: 'right', padding: '0.5rem' }}>{t('analytics.retention.profilePct', 'Profile %')}</th>
                        <th style={{ textAlign: 'right', padding: '0.5rem' }}>{t('analytics.retention.messages', 'Messages')}</th>
                        <th style={{ textAlign: 'right', padding: '0.5rem' }}>{t('analytics.retention.daysGone', 'Days Gone')}</th>
                        <th style={{ textAlign: 'left', padding: '0.5rem' }}>{t('analytics.retention.missingSteps', 'Missing Steps')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {retentionData.churnedProfiles.slice(0, 25).map((user) => (
                        <tr key={user.uid} style={{ borderBottom: '1px solid var(--border-color, #e0e0e0)' }}>
                          <td style={{ padding: '0.5rem' }} title={user.email}>
                            {user.name !== '—' ? user.name : user.email.slice(0, 12) + '…'}
                          </td>
                          <td style={{ padding: '0.5rem' }}>{user.signupDate}</td>
                          <td style={{ textAlign: 'right', padding: '0.5rem' }}>
                            <span style={{ color: user.profileCompletion < 50 ? '#F44336' : '#FF9800' }}>
                              {user.profileCompletion}%
                            </span>
                          </td>
                          <td style={{ textAlign: 'right', padding: '0.5rem' }}>{user.totalMessages}</td>
                          <td style={{ textAlign: 'right', padding: '0.5rem', color: user.daysSinceLastVisit > 30 ? '#F44336' : '#FF9800' }}>
                            {user.daysSinceLastVisit >= 999 ? t('analytics.retention.never', 'Never') : user.daysSinceLastVisit}
                          </td>
                          <td style={{ padding: '0.5rem', fontSize: '0.8rem', color: '#888' }}>
                            {user.missingSteps.join(', ') || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'retention' && !retentionData && !loading && (
          <div className="chart-placeholder">
            <div className="chart-icon">🔄</div>
            <p>{t('analytics.retention.noData', 'No retention data available.')}</p>
          </div>
        )}
      </div>

      {/* Refresh */}
      <div className="export-actions">
        <button onClick={loadData} className="export-btn refresh">
          🔄 {t('analytics.refresh')}
        </button>
      </div>
    </div>
  );
};

export default BusinessAnalytics;
